'use client'

import React from 'react'
import { Currency, ISection, ITheme, Language } from '@/lib/interfaces/interfaces'
import { NextFont } from 'next/dist/compiled/@next/font'
import ModernInfoBlock from './ModernInfoBlock'
import ModernImageBlock from './ModernImageBlock'
import ModernGoogleReviewBlock from './ModernGoogleReviewBlock'
import ModernItem from './ModernItem'
import AdSenseAd from './AdSenseAd'
import { getTextInLanguage } from '@/lib/utils/menuText'
import { getPriceLabel } from '@/lib/utils/priceLabels'
import '../../sass/menu/AdSection.scss'

interface Props {
  section: ISection
  theme: ITheme
  fontStyle: {
    fontFamily: NextFont
    fontSize: string
  }
  language: Language
  defaultLanguage?: Language
  currency: Currency
  orderingActive: boolean
  priceMarkup?: number
  showAds: boolean | undefined
  isPartner?: boolean
  partnerBadge?: string
}

const ModernSection = ({ section, theme, fontStyle, language, defaultLanguage, currency, orderingActive, priceMarkup = 0, showAds, isPartner, partnerBadge }: Props) => {
  // Key única para forzar re-renderizado y animaciones al cambiar de sección
  const sectionKey = `section-${section.sectionId}`

  if (section.type === 'infoBlock') {
    return (
      <ModernInfoBlock
        key={sectionKey}
        section={section}
        language={language}
        theme={theme}
        fontStyle={fontStyle}
      />
    )
  }

  if (section.type === 'imageSection') {
    return (
      <ModernImageBlock
        key={sectionKey}
        section={section}
        theme={theme}
        language={language}
        fontStyle={fontStyle}
      />
    )
  }

  if (section.type === 'googleReview') {
    return (
      <ModernGoogleReviewBlock
        key={sectionKey}
        section={section}
        theme={theme}
        language={language}
        fontStyle={fontStyle}
      />
    )
  }

  const visibleItems = section.menuItems.filter(item => {
    if (!item.visible) return false
    // En modo ordering, también filtrar por items disponibles para delivery
    if (orderingActive && item.availableForDelivery === false) return false
    return true
  })
  const sectionSubtitle = getTextInLanguage(section, 'subtitle', language)
  const sectionName = getTextInLanguage(section, 'sectionName', language)

  const sectionClass = isPartner ? 'modern-section modern-section--partner' : 'modern-section'

  return (
    <div className={sectionClass} key={sectionKey}>
      {isPartner && partnerBadge && (
        <span className='partner-badge'>{partnerBadge}</span>
      )}
      {isPartner && sectionName && (
        <div className='partner-section-title-wrap'>
          <h2 className='partner-section-title'>{sectionName}</h2>
        </div>
      )}
      {sectionSubtitle && (
        <div className='modern-section-header'>
          <p
            className={`modern-section-subtitle ${fontStyle.fontFamily.className}`}
            style={{
              fontSize: fontStyle.fontSize
            }}
          >
            {sectionSubtitle}
          </p>
        </div>
      )}
      <div className='modern-items-grid'>
        {visibleItems.map((item, index) => {
          const itemName = getTextInLanguage(item, 'name', language)
          const itemDescription = getTextInLanguage(item, 'description', language)

          const elements = []

          // Añadir el item actual
          elements.push(
            <div
              key={`${sectionKey}-${item.itemId}-${index}`}
              className='modern-item-wrapper'
              style={{ '--animation-index': index } as React.CSSProperties}
            >
              <ModernItem
                name={itemName}
                description={itemDescription}
                price={item.price}
                halfprice={item.halfprice}
                tapaprice={item.tapaprice}
                priceLabel={getPriceLabel(section, 'price', language, defaultLanguage)}
                halfpriceLabel={getPriceLabel(section, 'halfprice', language, defaultLanguage)}
                tapapriceLabel={getPriceLabel(section, 'tapaprice', language, defaultLanguage)}
                allergens={item.allergens}
                imageSource={item.img?.url}
                theme={theme}
                fontStyle={fontStyle}
                currency={currency}
                language={language}
                orderingActive={orderingActive}
                priceMarkup={priceMarkup}
                itemId={item.itemId}
              />
            </div>
          )

          // Añadir anuncio cada 4 items (después del item 4, 8, 12, etc.) solo si showAds es true
          if (showAds && (index + 1) % 4 === 0 && index < visibleItems.length - 1) {
            elements.push(
              <div
                key={`${sectionKey}-ad-${index}`}
                className='modern-item-wrapper modern-ad-wrapper'
                style={{ '--animation-index': index + 0.5 } as React.CSSProperties}
              >
                <AdSenseAd adSlot='7004524833' />
              </div>
            )
          }

          return elements
        }).flat()}
      </div>
    </div>
  )
}

export default ModernSection
